import React, {
  useEffect, useRef, useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../shared/_TBComponents/Button';
import Icon from '../../../shared/_TBComponents/Icon';
import Text from '../../../shared/_TBComponents/Text';
import ManageProgramsTable from './presentational/tables/ManageProgramsTable';
import ManageProgramTagsTable from './presentational/tables/ManageProgramTagsTable';
import EditProgramModal from './presentational/EditProgramModal';
import ShareProgramModal from './presentational/ShareProgramModal';
import AddImportProgramModal from './presentational/AddImportProgramModal';
import ProgramTemplatesModal from './presentational/ProgramTemplatesModal';
import ProgramTagModal from './presentational/modals/ProgramTagModal';
import ConfirmModal from './presentational/modals/ConfirmModal';
import LoadToCalendarModal from './presentational/modals/LoadToCalendarModal';
import TagFilterDropdown from './presentational/TagFilterDropdown';
import {
  fetchSavedWorkouts,
  deleteSavedWorkout,
  bulkDeleteSavedWorkouts,
  setProgramType,
  setSelectedReduxNonDateBasedProgram,
  fetchAllProgramTags,
  deleteProgramTags,
} from '../../calendar/ducks/calendarActions';

// Full-screen page mounted at /dashboard/manage-programs, where Navigator hides
// the sidebar chrome.

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #F5F5F5;
  overflow: hidden;
`;

const TopSection = styled.div`
  background: #FFF;
  padding: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #77A7FF;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

const BottomSection = styled.div`
  flex: 1;
  min-height: 0;
  background: #FFF;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

// Tab strip mirrors Manage Athletes/Exercises: divider under the tabs + spacing
// above the search.
const TabsRow = styled.div`
  display: flex;
  margin: 0 16px 16px 16px;
  box-sizing: border-box;
  flex-shrink: 0;
  border-bottom: 1px solid #D6D6D6;
`;

const TabButton = styled.button`
  padding-bottom: 8px;
  margin-right: 24px;
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: ${(props) => (props.active ? 700 : 400)};
  color: #1A1A1A;
  border: none;
  border-bottom: 3px solid ${(props) => (props.active ? '#1787FB' : 'transparent')};
  background: none;
  cursor: pointer;
  margin-bottom: -1px;
  transition: color 0.15s, border-color 0.15s;

  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const getBackLabel = (backPath, isMobile) => {
  if (isMobile && backPath.startsWith('/dashboard/programs')) return 'Back to Programs';
  if (backPath.startsWith('/dashboard/manage')) return 'Back to Manage';
  return 'Back to Home';
};

const ManagePrograms = ({ backPath, isMobile }) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const tableRef = useRef(null);
  const accountCode = useSelector((state) => state.auth.data.currentUser?.accountCode);
  const tagsForAllPrograms = useSelector((state) => state.calendar.data.tagsForAllPrograms);

  const [isLoading, setIsLoading] = useState(true);
  const [savedWorkoutsError, setSavedWorkoutsError] = useState(false);
  const [programToEdit, setProgramToEdit] = useState(null);
  const [programToShare, setProgramToShare] = useState(null);
  const [programToLoad, setProgramToLoad] = useState(null);
  const [programToDelete, setProgramToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [bulkDeleteIds, setBulkDeleteIds] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isTemplatesOpen, setIsTemplatesOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('programs');
  const [tagsLoading, setTagsLoading] = useState(true);
  const [tagsError, setTagsError] = useState(false);
  const [tagToEdit, setTagToEdit] = useState(null);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);
  const [isDeletingTag, setIsDeletingTag] = useState(false);
  const [bulkDeleteTagIds, setBulkDeleteTagIds] = useState([]);
  const [isBulkDeletingTags, setIsBulkDeletingTags] = useState(false);
  const [tagsClearSelectionToken, setTagsClearSelectionToken] = useState(0);
  const [programTagFilterIds, setProgramTagFilterIds] = useState([]);

  const addProgramOptions = [
    {
      key: 'build',
      icon: 'build',
      title: 'Build your own',
      description: 'Start a new program from scratch in Build',
      onClick: () => history.push('/program/create', { from: location.pathname }),
    },
    {
      key: 'templates',
      icon: 'templates',
      title: 'Browse our templates',
      description: 'Import a pre-built program from our template library.',
      onClick: () => setIsTemplatesOpen(true),
    },
    {
      key: 'ai',
      icon: 'sparkle',
      title: 'AI Import',
      description: 'Import your program from an existing file.',
      onClick: () => history.push('/import/program'),
    },
  ];

  const loadPrograms = () => {
    setIsLoading(true);
    setSavedWorkoutsError(false);
    dispatch(fetchSavedWorkouts(accountCode, {
      onSuccess: () => setIsLoading(false),
      onError: () => { setSavedWorkoutsError(true); setIsLoading(false); },
    }));
  };

  const loadTags = () => {
    setTagsLoading(true);
    setTagsError(false);
    dispatch(fetchAllProgramTags(accountCode, {
      onSuccess: () => setTagsLoading(false),
      onError: () => { setTagsError(true); setTagsLoading(false); },
    }));
  };

  useEffect(() => {
    if (!accountCode) return;
    loadPrograms();
    loadTags();
    // loadPrograms/loadTags are recreated every render (not memoized), so
    // listing them as deps would re-fire this on every render instead of
    // just once when accountCode becomes available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountCode, dispatch]);

  // Deep link from a program's own panel manage button
  useEffect(() => {
    const scrollToProgramId = location.state?.scrollToProgramId;
    if (scrollToProgramId == null || isLoading) return;
    setActiveTab('programs');
    tableRef.current?.scrollToProgram(scrollToProgramId);
    history.replace(location.pathname, { ...location.state, scrollToProgramId: undefined });
  }, [location.state?.scrollToProgramId, isLoading]);

  // Drop active filter ids whose tag no longer exists (deleted on the Tags
  // tab) so no ghost chips or dead filters linger.
  useEffect(() => {
    setProgramTagFilterIds((prev) => {
      const valid = new Set((tagsForAllPrograms || []).map((t) => t.id));
      const next = prev.filter((id) => valid.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [tagsForAllPrograms]);

  const handleLoadToCalendar = (program) => {
    setProgramToLoad(program);
  };

  const handleBuild = (program) => {
    dispatch(setProgramType('free-form-program'));
    dispatch(setSelectedReduxNonDateBasedProgram(program));
    history.push('/build');
  };

  const handleConfirmDelete = () => {
    if (!programToDelete) return;
    setIsDeleting(true);
    dispatch(deleteSavedWorkout(accountCode, programToDelete.id, {
      onSuccess: () => { setIsDeleting(false); setProgramToDelete(null); },
      onError: () => setIsDeleting(false),
    }));
  };

  const handleConfirmBulkDelete = () => {
    if (!bulkDeleteIds.length) return;
    setIsBulkDeleting(true);
    dispatch(bulkDeleteSavedWorkouts(accountCode, bulkDeleteIds, {
      onSuccess: () => { setIsBulkDeleting(false); setBulkDeleteIds([]); },
      onError: () => setIsBulkDeleting(false),
    }));
  };

  const handleNewTag = () => {
    setTagToEdit(null);
    setIsTagModalOpen(true);
  };

  const handleEditTag = (t) => {
    setTagToEdit(t);
    setIsTagModalOpen(true);
  };

  // The PUT response doesn't include a refreshed `programs` array, so refetch
  // tags for the chips; program rows' tag chips can also change, so refetch those too.
  const handleTagSaved = () => {
    loadTags();
    loadPrograms();
  };

  const handleConfirmDeleteTag = () => {
    if (!tagToDelete) return;
    setIsDeletingTag(true);
    dispatch(deleteProgramTags(accountCode, [tagToDelete.id], {
      onSuccess: () => {
        setIsDeletingTag(false);
        setTagToDelete(null);
        setTagsClearSelectionToken((t) => t + 1);
        loadPrograms();
      },
      onError: () => setIsDeletingTag(false),
    }));
  };

  const handleConfirmBulkDeleteTags = () => {
    if (!bulkDeleteTagIds.length) return;
    setIsBulkDeletingTags(true);
    dispatch(deleteProgramTags(accountCode, bulkDeleteTagIds, {
      onSuccess: () => {
        setIsBulkDeletingTags(false);
        setBulkDeleteTagIds([]);
        setTagsClearSelectionToken((t) => t + 1);
        loadPrograms();
      },
      onError: () => setIsBulkDeletingTags(false),
    }));
  };

  return (
    <Container>
      <TopSection>
        <TitleGroup>
          <BackLink type='button' onClick={() => history.push(backPath)}>
            <Icon icon='left-arrow' size={12} color='#77A7FF' />
            {getBackLabel(backPath, isMobile)}
          </BackLink>
          <Text styledTextProps={{ fontSize: '24px', fontWeight: 900, color: '#1A1A1A' }}>
            Manage Programs
          </Text>
        </TitleGroup>
        <ActionButtonGroup>
          {activeTab === 'tags' ? (
            <Button
              name='New Tag'
              icon='plus'
              iconColor='#FFF'
              bgColor='#1A1A1A'
              borderColor='#1A1A1A'
              txtColor='#FFF'
              customBorderRadius='4px'
              fontSize='14px'
              styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
              onClick={handleNewTag}
            />
          ) : (
            <Button
              name='Add/Import'
              icon='add-program'
              iconColor='#FFF'
              bgColor='#1A1A1A'
              borderColor='#1A1A1A'
              txtColor='#FFF'
              customBorderRadius='4px'
              fontSize='14px'
              styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
              onClick={() => setIsAddOpen(true)}
            />
          )}
        </ActionButtonGroup>
      </TopSection>

      <BottomSection>
        <TabsRow>
          <TabButton type='button' active={activeTab === 'programs'} onClick={() => setActiveTab('programs')}>
            Programs
          </TabButton>
          <TabButton type='button' active={activeTab === 'tags'} onClick={() => setActiveTab('tags')}>
            Tags
          </TabButton>
        </TabsRow>

        {activeTab === 'tags' ? (
          <ManageProgramTagsTable
            tags={[...(tagsForAllPrograms || [])]}
            isLoading={tagsLoading}
            hasError={tagsError}
            onRetry={loadTags}
            onEditTag={handleEditTag}
            onDeleteTag={setTagToDelete}
            onBulkDelete={setBulkDeleteTagIds}
            clearSelectionToken={tagsClearSelectionToken}
          />
        ) : (
          <ManageProgramsTable
            ref={tableRef}
            isLoading={isLoading}
            hasError={savedWorkoutsError}
            onRetry={loadPrograms}
            onEditProgram={setProgramToEdit}
            onBuildProgram={handleBuild}
            onLoadToCalendarProgram={handleLoadToCalendar}
            onShareProgram={setProgramToShare}
            onDeleteProgram={setProgramToDelete}
            onBulkDelete={setBulkDeleteIds}
            tagFilterIds={programTagFilterIds}
            filterControl={(
              <TagFilterDropdown
                tags={tagsForAllPrograms || []}
                selectedIds={programTagFilterIds}
                onChange={setProgramTagFilterIds}
              />
            )}
          />
        )}
      </BottomSection>

      <EditProgramModal
        isOpen={!!programToEdit}
        program={programToEdit}
        accountCode={accountCode}
        onClose={() => setProgramToEdit(null)}
      />

      <ShareProgramModal
        isOpen={!!programToShare}
        program={programToShare}
        accountCode={accountCode}
        onClose={() => setProgramToShare(null)}
      />

      <LoadToCalendarModal
        isOpen={!!programToLoad}
        program={programToLoad}
        accountCode={accountCode}
        onClose={() => setProgramToLoad(null)}
      />

      <AddImportProgramModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        options={addProgramOptions}
      />

      <ProgramTemplatesModal
        isOpen={isTemplatesOpen}
        onClose={() => setIsTemplatesOpen(false)}
        onBack={() => { setIsTemplatesOpen(false); setIsAddOpen(true); }}
        accountCode={accountCode}
      />

      <ConfirmModal
        isOpen={!!programToDelete}
        onClose={() => setProgramToDelete(null)}
        onConfirm={handleConfirmDelete}
        title='Delete Program'
        message={`Are you sure you want to delete "${programToDelete?.programName || 'this program'}"? This can't be undone.`}
        isLoading={isDeleting}
      />

      <ConfirmModal
        isOpen={bulkDeleteIds.length > 0}
        onClose={() => setBulkDeleteIds([])}
        onConfirm={handleConfirmBulkDelete}
        title='Delete Programs'
        message={`Are you sure you want to delete ${bulkDeleteIds.length} program${bulkDeleteIds.length === 1 ? '' : 's'}? This can't be undone.`}
        isLoading={isBulkDeleting}
      />

      <ProgramTagModal
        isOpen={isTagModalOpen}
        onClose={() => setIsTagModalOpen(false)}
        accountCode={accountCode}
        tag={tagToEdit}
        onSaved={handleTagSaved}
      />

      <ConfirmModal
        isOpen={!!tagToDelete}
        onClose={() => setTagToDelete(null)}
        onConfirm={handleConfirmDeleteTag}
        title='Delete Tag'
        message={`Are you sure you want to delete "${tagToDelete?.name || 'this tag'}"? It will be removed from all programs. This can't be undone.`}
        isLoading={isDeletingTag}
      />

      <ConfirmModal
        isOpen={bulkDeleteTagIds.length > 0}
        onClose={() => setBulkDeleteTagIds([])}
        onConfirm={handleConfirmBulkDeleteTags}
        title='Delete Tags'
        message={`Are you sure you want to delete ${bulkDeleteTagIds.length} tag${bulkDeleteTagIds.length === 1 ? '' : 's'}? They will be removed from all programs. This can't be undone.`}
        isLoading={isBulkDeletingTags}
      />
    </Container>
  );
};

ManagePrograms.propTypes = {
  backPath: PropTypes.string,
  isMobile: PropTypes.bool,
};

ManagePrograms.defaultProps = {
  backPath: '/dashboard/programs',
  isMobile: false,
};

export default ManagePrograms;
